import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons/faExternalLinkAlt';

import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { emptyObject } from 'ui-components/default-props/empty';
import { FlexRow, FlexColumn } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import { ACTION, filterButtons, NAVIGATION, POP_OVER_VIDEO, TUTORIAL, ARTICLE } from './card-data';
import HelpArticleLink from '../../../../components/help-article-link';
import { HomePageFeedCompletedCards } from './home-page-constants';
import { MessageDescriptorPropType } from '../../../../prop-types/intl';
import { NavigationButton } from './engagement-action-buttons/navigation-button';
import { useSaveCardCompleteState } from '../../hooks/use-card-complete-state';
import { useTrackHomePageFeedCards } from '../../hooks/use-track-home-page-feed-cards';
import { useUserSetting } from '../../../../hooks/use-user-setting';
import { VideoButton } from './video-button';
import { WistiaVideo } from './wistia-video';

import { messages } from './messages';
import style from './engagement-card.module.less';

const TimeToComplete = ({ length }) => {
  return (
    <>
      <FontAwesomeIcon className={style['clock-icon']} icon={faClock} />
      <Text textType={TextType.Caption} value={length} />
    </>
  );
};

TimeToComplete.propTypes = {
  length: MessageDescriptorPropType.isRequired,
};

export const EngagementCard = ({ card }) => {
  const {
    id,
    title,
    length,
    description,
    descriptionValue = emptyObject,
    customBody,
    videoId,
    buttonText,
    actionButton,
    cardType,
    tag,
    kbUrl,
    image,
    navigationTarget,
  } = card;
  const intl = useIntl();
  const saveCompleteState = useSaveCardCompleteState(id);
  const userHomePageFeedCompletedCards = useUserSetting(HomePageFeedCompletedCards) || '[]';
  const completeCards = JSON.parse(userHomePageFeedCompletedCards) || [];
  const trackHomePageCardInteraction = useTrackHomePageFeedCards(id);
  const isCardCompleted = completeCards.includes(id);
  const tagText = useMemo(() => {
    const button = filterButtons.find((tagObject) => tagObject.tag === tag);
    return button ? intl.formatMessage(button.tagText) : '';
  }, [tag, intl]);

  const buttonClicked = useCallback(
    (buttonType) => {
      saveCompleteState();
      trackHomePageCardInteraction(buttonType);
    },
    [saveCompleteState, trackHomePageCardInteraction],
  );

  const onArticleClicked = useCallback(() => buttonClicked(ARTICLE), [buttonClicked]);
  const onActionClicked = useCallback(() => buttonClicked(ACTION), [buttonClicked]);
  const onNavigationClicked = useCallback(() => buttonClicked(NAVIGATION), [buttonClicked]);
  const onVideoClicked = useCallback(() => buttonClicked(TUTORIAL), [buttonClicked]);
  const onWistiaClick = useCallback(() => buttonClicked(POP_OVER_VIDEO), [buttonClicked]);

  const [isLoading, setIsLoading] = useState(false);

  const stateToRender = isCardCompleted ? <Text textType={TextType.Caption} value={messages.viewed} /> : <TimeToComplete length={length} />;

  const buttonToRender = () => {
    if (!buttonText) {
      return null;
    }
    if (cardType === ACTION) {
      return actionButton(onActionClicked, setIsLoading);
    }
    if (cardType === NAVIGATION) {
      return <NavigationButton buttonText={buttonText} navigationTarget={navigationTarget} onNavigationClicked={onNavigationClicked} />;
    }
    if (videoId) {
      return <VideoButton onVideoClicked={onVideoClicked} videoId={videoId} />;
    }
    return null;
  };

  if (isLoading) {
    return (
      <FlexColumn className={style.card}>
        <LoadingIndicator className={style['loading-indicator']} />
        <Text className={style['loading-text']} textType={TextType.H2} value={messages.loadingIndicator} />
      </FlexColumn>
    );
  }

  return (
    <div className={style.card}>
      <FlexRow className={style.title}>
        <FlexRow className={style['card-header']}>
          <Text className={style['card-header-text']} textType={TextType.H2} value={title} />
          <Text className={style.tag} textType={TextType.Caption} value={tagText} />
        </FlexRow>
        <FlexRow className={style.length}>{stateToRender}</FlexRow>
      </FlexRow>
      <div className={style.body}>
        <FlexRow>
          {description && (
            <Text
              className={style['card-text']}
              formattedMessageValues={descriptionValue}
              textType={TextType.Paragraph}
              value={description}
            />
          )}
          {customBody}
          <FlexRow className={style['card-buttons']}>
            {buttonToRender()}
            {kbUrl !== null && (
              <FlexRow className={style['link-and-icon']}>
                <HelpArticleLink articleId={kbUrl} className={style['read-article']} onClick={onArticleClicked}>
                  <FormattedMessage {...messages.readArticle} />
                </HelpArticleLink>
                <FontAwesomeIcon className={style['external-link-icon']} icon={faExternalLinkAlt} />
              </FlexRow>
            )}
          </FlexRow>
        </FlexRow>
        <div>
          {videoId && <WistiaVideo onWistiaClick={onWistiaClick} videoId={videoId} />}
          {image && <img alt={intl.formatMessage(title)} className={style.image} src={image} />}
        </div>
      </div>
    </div>
  );
};

EngagementCard.propTypes = {
  card: PropTypes.shape({
    id: PropTypes.number.isRequired,
    actionButton: PropTypes.func,
    cardType: PropTypes.string.isRequired,
    buttonText: MessageDescriptorPropType,
    title: MessageDescriptorPropType.isRequired,
    length: MessageDescriptorPropType.isRequired,
    description: MessageDescriptorPropType,
    customBody: PropTypes.node,
    videoId: PropTypes.string,
    buttonAction: PropTypes.func,
    kbUrl: PropTypes.string.isRequired,
    tag: PropTypes.string.isRequired,
    image: PropTypes.string,
    navigationTarget: PropTypes.string,
  }).isRequired,
};
